import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdSearch, MdAdd, MdCheck } from 'react-icons/md';
import { signalsService } from '../../../services/signalsService';

/**
 * Search box with typeahead suggestions.
 *
 *  - As the user types, GET /api/signals/search/?q=… returns top matches
 *    (debounced 200ms) ranked by ticker prefix, ticker contains, then name
 *    contains. Each suggestion shows whether we already cover it.
 *  - Click a suggestion → navigate to /signals/symbol/<TICKER>.
 *  - Enter → navigate using whatever's typed (treat as direct ticker).
 *  - "Add to Coverage" → POST /api/signals/add/ to fetch + compute on demand
 *    (used when the ticker is something we don't yet have).
 */

const DEBOUNCE_MS = 200;

const SymbolSearchAdd = ({ className = '' }) => {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState(null);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  // Debounced fetch — keep the most recent request only (drop stale responses
  // by stamping each call with an incrementing id).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = value.trim();
    if (trimmed.length < 1) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const myId = ++reqIdRef.current;
      try {
        const res = await signalsService.search(trimmed, 10);
        if (myId !== reqIdRef.current) return;  // stale response
        setSuggestions(res.data?.results || []);
      } catch {
        if (myId !== reqIdRef.current) return;
        setSuggestions([]);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  // Click outside to close
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const goToSymbol = useCallback((sym) => {
    setShowSuggestions(false);
    setMessage(null);
    navigate(`/signals/symbol/${encodeURIComponent(sym)}`);
  }, [navigate]);

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (highlightIdx >= 0 && suggestions[highlightIdx]) {
      goToSymbol(suggestions[highlightIdx].symbol);
      return;
    }
    const sym = value.trim().toUpperCase();
    if (!sym) return;
    goToSymbol(sym);
  };

  const handleKey = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleAdd = async () => {
    const sym = value.trim().toUpperCase();
    if (!sym) {
      setMessage({ kind: 'error', text: 'Enter a ticker first.' });
      return;
    }
    if (!/^[A-Z0-9.\-]{1,20}$/.test(sym)) {
      setMessage({ kind: 'error', text: 'Invalid ticker format.' });
      return;
    }
    setAdding(true);
    setShowSuggestions(false);
    setMessage({ kind: 'info', text: `Fetching ${sym} from yfinance + FMP — this can take 10–30s…` });
    try {
      const res = await signalsService.addSymbol(sym);
      const already = res.data?.already_covered;
      setMessage({
        kind: 'info',
        text: already ? `${sym} already covered. Opening…` : `${sym} added. Opening…`,
      });
      setTimeout(() => navigate(`/signals/symbol/${encodeURIComponent(sym)}`), 400);
    } catch (e) {
      setMessage({
        kind: 'error',
        text: e.response?.data?.error || `Could not add ${sym}.`,
      });
    } finally {
      setAdding(false);
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <div className="relative flex-1 md:max-w-md">
          <MdSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
          <input
            type="text"
            value={value}
            onChange={(e) => { setValue(e.target.value); setShowSuggestions(true); setHighlightIdx(-1); }}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={handleKey}
            placeholder="Search ticker or name (NVDA, Apple, Tesla…)"
            className="w-full pl-8 pr-2 py-1.5 text-xs bg-transparent border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100 placeholder-gray-400"
            autoComplete="off"
          />
        </div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={adding || !value.trim()}
          title="Fetch OHLCV from yfinance/FMP and compute signals"
          className="text-xs px-3 py-1.5 rounded bg-[#c9a96e] text-white hover:bg-[#b8985d] disabled:opacity-40 flex items-center gap-1 whitespace-nowrap"
        >
          {adding ? (
            <span className="inline-block w-3 h-3 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
          ) : (
            <MdAdd />
          )}
          <span className="hidden md:inline">{adding ? 'Adding…' : 'Add to Coverage'}</span>
          <span className="md:hidden">{adding ? '…' : 'Add'}</span>
        </button>
      </form>

      {/* Suggestion dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 left-0 right-0 md:right-auto md:w-[28rem] mt-1 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button
              key={s.symbol}
              type="button"
              onClick={() => goToSymbol(s.symbol)}
              onMouseEnter={() => setHighlightIdx(i)}
              className={`w-full text-left px-3 py-2 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 last:border-b-0 ${
                highlightIdx === i ? 'bg-gray-50 dark:bg-gray-900/60' : 'hover:bg-gray-50 dark:hover:bg-gray-900/60'
              }`}
            >
              <span className="font-semibold text-[#c9a96e] w-16 shrink-0 text-sm">{s.symbol}</span>
              <span className="flex-1 min-w-0">
                <span className="block text-xs text-gray-900 dark:text-gray-100 truncate">{s.name}</span>
                <span className="block text-[10px] text-gray-500 dark:text-gray-400 truncate">
                  {s.sector || (s.is_etf ? 'ETF' : '—')}
                </span>
              </span>
              {s.covered ? (
                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 flex items-center gap-1 shrink-0">
                  <MdCheck size={10} /> covered
                </span>
              ) : (
                <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 shrink-0">
                  needs add
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {message && (
        <div className={`text-[11px] mt-1.5 ${message.kind === 'error' ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>
          {message.text}
        </div>
      )}
    </div>
  );
};

export default SymbolSearchAdd;
